import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  CheckCircle2,
  Download,
  ExternalLink,
  MoreVertical,
  Pencil,
  Plus,
  Trash2,
  Upload,
  UserCheck,
  XCircle,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DangerousConfirmDialog } from '@/components/ui/dangerous-confirm-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PageHeader } from '@/components/ui/page-header';
import { BulkActionBar, BulkButton } from '@/components/data-table/BulkActionBar';
import {
  SelecteurSousSegmentFormation,
  useSousSegmentsFormation,
  type FormationSegmentValue,
} from '@/components/formation/SelecteurSousSegmentFormation';
import {
  exportToCSV,
  useBulkInscriptions,
  useFormations,
  useInscriptions,
  useSessions,
  useUpsertInscription,
} from '@/hooks/queries/useFormation';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import { useTiers } from '@/hooks/queries/useTiers';
import { useUsersList } from '@/hooks/queries/useUsersList';
import { useRole } from '@/hooks/useRole';
import { formatDA, formatDateDZ } from '@/lib/format';
import { cn } from '@/lib/utils';
import { t } from '@/locales/i18n';
import type { InscriptionFormationRow, InscriptionStatut } from '@/lib/database.types';
import { InscriptionFormDialog } from './InscriptionFormDialog';
import { InscriptionBulkDialog } from './InscriptionBulkDialog';
import { ImportDialog, type ImportField } from '@/components/import/ImportDialog';
import { buildTemplate, TEMPLATE_INSCRIPTIONS } from '@/lib/import-templates';

export function InscriptionsPage(): JSX.Element {
  const { isAdmin } = useRole();
  const { data: entreprises = [] } = useEntreprises();
  const eurlKostGroup = entreprises.find((e) => e.nom.includes('Kost'));
  const { data: users = [] } = useUsersList();

  const [segment, setSegment] = useState<FormationSegmentValue>('all');
  const { ids: segmentIds } = useSousSegmentsFormation(segment);
  const [statut, setStatut] = useState<InscriptionStatut | ''>('');

  const { data: formations = [] } = useFormations({ sousSegmentIds: segmentIds });
  const formationIds = useMemo(() => formations.map((f) => f.id), [formations]);
  const { data: allSessions = [] } = useSessions({});
  const sessionIdsScoped = useMemo(
    () => allSessions.filter((s) => formationIds.includes(s.formation_id)).map((s) => s.id),
    [allSessions, formationIds],
  );

  const { data, isLoading } = useInscriptions({ statut: statut || null, pageSize: 200 });
  const rows = useMemo(() => {
    const all = data?.rows ?? [];
    return formationIds.length > 0
      ? all.filter((i) => sessionIdsScoped.includes(i.session_id))
      : all;
  }, [data, sessionIdsScoped, formationIds]);

  const sessionMap = useMemo(() => {
    const m = new Map<string, { ref: string; formation_id: string; date: string }>();
    for (const s of allSessions)
      m.set(s.id, { ref: s.reference, formation_id: s.formation_id, date: s.date_debut });
    return m;
  }, [allSessions]);
  const formationMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of formations) m.set(f.id, f.titre);
    return m;
  }, [formations]);
  const { data: tiers = [] } = useTiers({ entrepriseId: eurlKostGroup?.id ?? null, search: '' });
  const tierMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const tt of tiers) m.set(tt.id, tt.raison_sociale);
    return m;
  }, [tiers]);

  const upsert = useUpsertInscription();
  const bulk = useBulkInscriptions();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState<InscriptionFormationRow | null>(null);

  // Bulk
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkConfirmDelete, setBulkConfirmDelete] = useState(false);
  const [bulkAssignTo, setBulkAssignTo] = useState('');
  const clearSelection = (): void => setSelected(new Set());
  const toggleSelect = (id: string): void => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectAll = (): void => setSelected(new Set(rows.map((r) => r.id)));
  const selectedRows = rows.filter((r) => selected.has(r.id));

  const handleAnnule = async (i: InscriptionFormationRow): Promise<void> => {
    await upsert.mutateAsync({
      id: i.id,
      session_id: i.session_id,
      tier_id: i.tier_id,
      prix_facture: i.prix_facture,
      statut: 'annule',
    });
    toast.success(`Inscription ${i.reference} annulée`);
  };

  useEffect(() => undefined, [eurlKostGroup]);

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        title="Inscriptions"
        subtitle={`${rows.length} inscription(s) au total`}
        actions={
          <div className="flex items-center gap-2">
            <SelecteurSousSegmentFormation value={segment} onChange={setSegment} />
            <select
              value={statut}
              onChange={(e) => setStatut(e.target.value as InscriptionStatut | '')}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="">— Tous statuts —</option>
              {(
                [
                  'pre_inscrit',
                  'confirme',
                  'paye_partiel',
                  'paye_total',
                  'annule',
                  'liste_attente',
                ] as InscriptionStatut[]
              ).map((s) => (
                <option key={s} value={s}>
                  {t(`formation.inscription.statut.${s}`)}
                </option>
              ))}
            </select>
            <Button variant="outline" onClick={() => setImportOpen(true)}>
              <Upload className="h-4 w-4" /> Importer XLSX/CSV
            </Button>
            <Button variant="outline" onClick={() => setBulkOpen(true)}>
              Inscription en masse
            </Button>
            <Button
              onClick={() => {
                setEditing(null);
                setDialogOpen(true);
              }}
            >
              <Plus className="h-4 w-4" /> Nouvelle inscription
            </Button>
          </div>
        }
      />

      <BulkActionBar count={selected.size} onClear={clearSelection}>
        <BulkButton onClick={selectAll}>Tout ({rows.length})</BulkButton>
        <BulkButton
          onClick={async () => {
            const r = await bulk.mutateAsync({ ids: [...selected], action: 'confirmer' });
            toast.success(`${r.success} inscription(s) confirmée(s)`);
            clearSelection();
          }}
        >
          <CheckCircle2 className="h-3.5 w-3.5" /> Confirmer
        </BulkButton>
        <BulkButton
          onClick={async () => {
            const r = await bulk.mutateAsync({ ids: [...selected], action: 'annuler' });
            toast.success(`${r.success} inscription(s) annulée(s)`);
            clearSelection();
          }}
        >
          <XCircle className="h-3.5 w-3.5" /> Annuler
        </BulkButton>
        <div className="flex items-center gap-1">
          <select
            value={bulkAssignTo}
            onChange={(e) => setBulkAssignTo(e.target.value)}
            className="h-7 rounded border border-input bg-background px-2 text-xs"
          >
            <option value="">— Réassigner à —</option>
            {users.map((u) => (
              <option key={u.user_id} value={u.user_id}>
                {u.full_name ?? u.user_id.slice(0, 8)}
              </option>
            ))}
          </select>
          <BulkButton
            disabled={!bulkAssignTo}
            onClick={async () => {
              const r = await bulk.mutateAsync({
                ids: [...selected],
                action: 'change_commercial',
                commercial_id: bulkAssignTo,
              });
              toast.success(`${r.success} inscription(s) réassignée(s)`);
              clearSelection();
              setBulkAssignTo('');
            }}
          >
            <UserCheck className="h-3.5 w-3.5" /> Appliquer
          </BulkButton>
        </div>
        <BulkButton
          onClick={() => {
            exportToCSV(
              selectedRows.map((i) => ({
                reference: i.reference,
                tier: tierMap.get(i.tier_id) ?? '',
                session: sessionMap.get(i.session_id)?.ref ?? '',
                statut: i.statut,
                prix: i.prix_facture,
                acompte_pct: i.acompte_pct,
                source: i.source ?? '',
              })),
              `inscriptions-${selectedRows.length}.csv`,
            );
            toast.success(`${selectedRows.length} ligne(s) exportée(s)`);
          }}
        >
          <Download className="h-3.5 w-3.5" /> Export
        </BulkButton>
        {isAdmin && (
          <BulkButton variant="destructive" onClick={() => setBulkConfirmDelete(true)}>
            <Trash2 className="h-3.5 w-3.5" /> Supprimer
          </BulkButton>
        )}
      </BulkActionBar>

      <div className="rounded-lg border border-border bg-card">
        <table className="w-full text-xs">
          <thead className="border-b border-border bg-muted/40 text-[10px] uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">
                <input
                  type="checkbox"
                  checked={selected.size === rows.length && rows.length > 0}
                  onChange={() => (selected.size === rows.length ? clearSelection() : selectAll())}
                />
              </th>
              <th className="px-3 py-2 text-left">Réf</th>
              <th className="px-3 py-2 text-left">Session</th>
              <th className="px-3 py-2 text-left">Formation</th>
              <th className="px-3 py-2 text-left">Tiers</th>
              <th className="px-3 py-2 text-left">Statut</th>
              <th className="px-3 py-2 text-right">Prix</th>
              <th className="px-3 py-2 text-left">Source</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={9} className="py-4 text-center text-muted-foreground">
                  Chargement…
                </td>
              </tr>
            )}
            {rows.map((i) => {
              const sess = sessionMap.get(i.session_id);
              return (
                <tr
                  key={i.id}
                  className={cn('border-b border-border/40', selected.has(i.id) && 'bg-primary/5')}
                >
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selected.has(i.id)}
                      onChange={() => toggleSelect(i.id)}
                    />
                  </td>
                  <td className="px-3 py-2 font-mono">{i.reference}</td>
                  <td className="px-3 py-2">
                    {sess ? (
                      <Link
                        to={`/formation/sessions/${i.session_id}`}
                        className="text-primary hover:underline"
                      >
                        {sess.ref}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {sess && formationMap.get(sess.formation_id)
                      ? formationMap.get(sess.formation_id)
                      : '—'}
                  </td>
                  <td className="px-3 py-2">{tierMap.get(i.tier_id) ?? '—'}</td>
                  <td className="px-3 py-2">
                    <Badge variant={statutVariant(i.statut)}>
                      {t(`formation.inscription.statut.${i.statut}`)}
                    </Badge>
                  </td>
                  <td className="num px-3 py-2 text-right">{formatDA(Number(i.prix_facture))}</td>
                  <td className="px-3 py-2">
                    {i.opportunite_id ? (
                      <Link
                        to={`/crm/opportunite/${i.opportunite_id}`}
                        className="inline-flex items-center gap-0.5 rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700 hover:bg-blue-100"
                        title="Ouvrir l'opportunité CRM"
                      >
                        <ExternalLink className="h-2.5 w-2.5" /> CRM
                      </Link>
                    ) : (
                      <span className="text-[10px] text-muted-foreground">
                        {i.source ?? 'manuelle'}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7">
                          <MoreVertical className="h-3.5 w-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onSelect={() => {
                            setEditing(i);
                            setDialogOpen(true);
                          }}
                        >
                          <Pencil className="h-3.5 w-3.5" /> Modifier
                        </DropdownMenuItem>
                        {i.acompte_echeance_id && (
                          <DropdownMenuItem asChild>
                            <Link to="/echeances/a-encaisser">Voir échéances liées</Link>
                          </DropdownMenuItem>
                        )}
                        {i.statut !== 'annule' && (
                          <DropdownMenuItem
                            onSelect={() => void handleAnnule(i)}
                            className="text-destructive"
                          >
                            <XCircle className="h-3.5 w-3.5" /> Annuler
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && !isLoading && (
              <tr>
                <td colSpan={9} className="py-4 text-center text-muted-foreground">
                  Aucune inscription.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <InscriptionFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        defaultSousSegmentIds={segmentIds.length > 0 ? segmentIds : undefined}
        editing={editing}
        onSaved={() => {
          setDialogOpen(false);
          setEditing(null);
        }}
      />

      <InscriptionBulkDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        defaultSousSegmentIds={segmentIds.length > 0 ? segmentIds : undefined}
        onSaved={() => setBulkOpen(false)}
      />

      <ImportDialog<Record<string, unknown>>
        open={importOpen}
        onOpenChange={setImportOpen}
        title="Importer des inscriptions (XLSX/CSV)"
        generateTemplate={() => buildTemplate(TEMPLATE_INSCRIPTIONS)}
        templateFilename={TEMPLATE_INSCRIPTIONS.filename}
        fields={
          [
            { key: 'reference', label: 'Réf facture' },
            { key: 'formation', label: 'Formation (titre)', required: true },
            { key: 'date_formation', label: 'Date formation', required: true },
            { key: 'lieu', label: 'Lieu' },
            { key: 'entreprise', label: 'Entreprise (client)', required: true },
            { key: 'nb_participants', label: 'Nb participants', required: true },
            { key: 'ht', label: 'HT (DA)', required: true },
            { key: 'tva', label: 'TVA (DA)' },
            { key: 'ttc', label: 'TTC (DA)', required: true },
            { key: 'statut', label: 'Statut', required: true },
            { key: 'date_reglement', label: 'Date règlement' },
            { key: 'mode_paiement', label: 'Mode paiement' },
            { key: 'reference_externe', label: 'Référence virement' },
            { key: 'commercial_email', label: 'Commercial (email)' },
            { key: 'observation', label: 'Observation' },
          ] satisfies ImportField[]
        }
        onImport={async (rows) => {
          // Import inscriptions complexe : la création auto session/formation/tier
          // se fera côté Edge Function ou hook dédié dans Sprint 5.4. Pour l'instant
          // on rejette les lignes dont la session n'existe pas avec un message clair.
          const errors: string[] = [];
          for (let i = 0; i < rows.length; i++) {
            const r = rows[i];
            if (!r) continue;
            errors.push(
              `L${i + 2}: import inscriptions complet (auto-création formation/session/tier) prévu Sprint 5.4 — utilisez "Inscription en masse" pour l'instant`,
            );
          }
          return { success: 0, errors };
        }}
      />

      <DangerousConfirmDialog
        open={bulkConfirmDelete}
        onOpenChange={setBulkConfirmDelete}
        title={`Supprimer ${selected.size} inscription${selected.size > 1 ? 's' : ''} ?`}
        description="Action irréversible. Les échéances liées sont conservées (pas de cascade)."
        consequences={[
          `${selected.size} inscription(s) effacées`,
          'Les présences liées sont supprimées en cascade',
          'Audit log conservé',
        ]}
        challenge="SUPPRIMER"
        loading={bulk.isPending}
        onConfirm={async () => {
          const r = await bulk.mutateAsync({ ids: [...selected], action: 'delete' });
          if (r.errors.length === 0) toast.success(`${r.success} inscription(s) supprimée(s)`);
          else
            toast.warning(`${r.success} OK · ${r.errors.length} échec(s) — ${r.errors[0] ?? ''}`);
          setBulkConfirmDelete(false);
          clearSelection();
        }}
      />

      <span className="hidden">{formatDateDZ('2026-01-01')}</span>
    </div>
  );
}

function statutVariant(
  s: InscriptionStatut,
): 'default' | 'success' | 'warning' | 'outline' | 'secondary' | 'destructive' {
  if (s === 'paye_total') return 'success';
  if (s === 'paye_partiel') return 'warning';
  if (s === 'confirme') return 'default';
  if (s === 'annule') return 'destructive';
  if (s === 'liste_attente') return 'secondary';
  return 'outline';
}
